import React from "react";
import {
  Box,
  Card,
  CardBody,
  Center,
  Divider,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Switch,
  Tag,
  Text,
  Tooltip,
  VStack,
  keyframes,
} from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { AppIcons } from "constants/icons";
import { getRelativeTimeString } from "utils/dateUtils";

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.6); }
  70%  { box-shadow: 0 0 0 5px rgba(72, 187, 120, 0); }
  100% { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0); }
`;

export interface JobListProps {
  jobs: Header[];
  isLoadingJobs: boolean;
  userId: string | undefined;
  copyingJobId: string | null;
  isDeleting: boolean;
  onJobClick?: (job: Header) => void;
  onToggleActive: (job: Header, isActive: boolean) => Promise<void>;
  onRunJob: (job: Header) => void;
  onCopyJob: (job: Header) => Promise<void>;
  onDeleteClick: (job: Header) => void;
}

const JobList: React.FC<JobListProps> = ({
  jobs,
  isLoadingJobs,
  userId,
  copyingJobId,
  isDeleting,
  onJobClick,
  onToggleActive,
  onRunJob,
  onCopyJob,
  onDeleteClick,
}) => {
  const yourJobs = jobs.filter(j => j.author_id === userId);
  const othersJobs = jobs.filter(j => j.author_id !== userId);

  const renderCard = (job: Header) => {
    const isLive = !!job.has_active_schedule;
    const modified_date = job.last_modified_date_ms ? getRelativeTimeString(job.last_modified_date_ms) : null;

    return (
      <Card
        key={job.id}
        cursor="pointer"
        _hover={{
          shadow: "md",
          borderColor: isLive ? "green.500" : "purple.400",
          bg: "gray.50",
        }}
        transition="all 0.2s"
        border="1px solid"
        borderColor={isLive ? "green.500" : "gray.500"}
        bg="white"
        borderRadius="md"
        onClick={() => onJobClick?.(job)}
      >
        <CardBody py={2.5} px={3}>
          <HStack justify="space-between" align="center" spacing={2}>
            {/* Left: icon + name + meta */}
            <HStack spacing={2} flex={1} minW={0} align="center">
              <Icon as={AppIcons.workflow} boxSize={7} color={isLive ? "green.500" : "purple.500"} flexShrink={0} />
              <Box flex={1} minW={0}>
                <Tooltip label={job.name} placement="top" openDelay={600}>
                  <Box display="block" mb={1}>
                    <Text fontSize="md" fontWeight="600" color="gray.800" mb={0} noOfLines={1} textAlign="left">
                      {job.name}
                    </Text>
                  </Box>
                </Tooltip>
                <HStack spacing={1} align="center" mt={1}>
                  {modified_date && (
                    <Text fontSize="xs" color="gray.400" mb={0} textAlign="left">
                      Last modified {modified_date}
                    </Text>
                  )}
                  {job.author_name && (
                    <Tooltip label={job.author_name} placement="bottom" openDelay={600}>
                      <Text fontSize="xs" color="gray.400" mb={0} noOfLines={1} maxW="280px" textAlign="left">
                        by {job.author_name}
                      </Text>
                    </Tooltip>
                  )}
                </HStack>
              </Box>
            </HStack>

            {/* Right: LIVE badge + menu */}
            <HStack spacing={1} flexShrink={0} align="center" onClick={e => e.stopPropagation()}>
              {isLive && (
                <HStack
                  spacing={1}
                  border="1.5px solid"
                  borderColor="green.500"
                  borderRadius="full"
                  px={2}
                  py="1px"
                  bg="green.50"
                >
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg="green.500"
                    flexShrink={0}
                    animation={`${pulse} 1.8s ease-in-out infinite`}
                  />
                  <Text fontSize="xs" fontWeight="800" color="green.700" mb={0} letterSpacing="0.05em">
                    LIVE
                  </Text>
                </HStack>
              )}

              <Menu placement="bottom-end" autoSelect={false}>
                <MenuButton
                  as={IconButton}
                  aria-label="Workflow options"
                  icon={<Icon as={AppIcons.menuDots} boxSize={4} />}
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={e => e.stopPropagation()}
                />
                <Portal>
                  <MenuList minW="190px" fontSize="sm" zIndex="dropdown">
                    <MenuItem closeOnSelect={false} onClick={e => e.stopPropagation()}>
                      <HStack justify="space-between" w="100%">
                        <Text mb={0} fontWeight="500">
                          {isLive ? "Live" : "Paused"}
                        </Text>
                        <Switch
                          size="sm"
                          colorScheme="green"
                          isChecked={isLive}
                          onChange={e => {
                            e.stopPropagation();
                            onToggleActive(job, !isLive);
                          }}
                        />
                      </HStack>
                    </MenuItem>
                    <MenuDivider my={0} />
                    <MenuItem
                      icon={<AppIcons.runWorkflow />}
                      onClick={e => {
                        e.stopPropagation();
                        onRunJob(job);
                      }}
                    >
                      Run Workflow
                    </MenuItem>
                    <MenuItem
                      icon={copyingJobId === job.id ? <Spinner size="xs" /> : <AppIcons.copy />}
                      onClick={e => {
                        e.stopPropagation();
                        onCopyJob(job);
                      }}
                      isDisabled={copyingJobId === job.id}
                    >
                      Duplicate
                    </MenuItem>
                    <MenuDivider my={0} />
                    <MenuItem
                      icon={<AppIcons.delete color="red.400" />}
                      color="red.500"
                      isDisabled={isDeleting}
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteClick(job);
                      }}
                    >
                      Delete
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
            </HStack>
          </HStack>
        </CardBody>
      </Card>
    );
  };

  const renderSectionHeader = (title: string, count: number) => (
    <HStack spacing={3} mb={3}>
      <Text fontSize="sm" fontWeight="700" color="gray.700" mb={0}>
        {title}
      </Text>
      <Tag size="sm" colorScheme="purple" borderRadius="full" fontWeight="600" bg="purple.600" color="white">
        {count}
      </Tag>
      <Divider flex="1" borderColor="gray.200" />
    </HStack>
  );

  return (
    <Box height="100%" overflowY="auto">
      <Box maxW="900px" mx="auto">
        {isLoadingJobs ? (
          <Box height="200px" display="flex" alignItems="center" justifyContent="center">
            <Spinner size="sm" color="purple.500" thickness="4px" />
          </Box>
        ) : jobs.length === 0 ? (
          <Box height="200px" display="flex" alignItems="center" justifyContent="center">
            <Text color="gray.500" fontSize="md" mb={0}>
              No workflows match your search or filter.
            </Text>
          </Box>
        ) : (
          <VStack spacing={6} align="stretch">
            {/* Your Workflows */}
            <Box>
              {renderSectionHeader("Your Workflows", yourJobs.length)}
              {yourJobs.length === 0 ? (
                <Center py={6}>
                  <Text color="gray.400" fontSize="sm" mb={0}>
                    Looks like you don't have any workflows yet. Create a new one to get started.
                  </Text>
                </Center>
              ) : (
                <VStack spacing={3} align="stretch">
                  {yourJobs.map(renderCard)}
                </VStack>
              )}
            </Box>

            {/* Others' Workflows */}
            {othersJobs.length > 0 && (
              <Box>
                {renderSectionHeader("Others' Workflows", othersJobs.length)}
                <VStack spacing={2} align="stretch">
                  {othersJobs.map(renderCard)}
                </VStack>
              </Box>
            )}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default JobList;
